import { isNumber } from "lodash";
import { UserPasswords } from "@linkfluence/models/user";
import { ProjectWithCorpora } from "@radarly/models/project";
import {
  CreateUser,
  DiscoveryPreferencePayload,
  DiscoveryPreferenceResponse,
  SearchUser,
  SearchUserRequest,
  SearchUserResponse,
  User,
} from "@radarly/models/user";
import { RadarlyAPI } from "./base";

type AbstractUser = number | User;
type Picture = number | { id: number };

const getId = (object: AbstractUser | Picture) => {
  if (isNumber(object)) {
    return object;
  } else {
    return object.id;
  }
};

export class UserAPI extends RadarlyAPI {
  public async getCurrentUser() {
    return await this.get<User>("/users.json");
  }
  public async updateUser(user: User["id"], payload: Partial<User>) {
    return await this.put<User>(`/users/${user}.json`, payload);
  }
  public async getProjectsWithCorpora() {
    return await this.get<ProjectWithCorpora[]>(
      "/users/project-with-corpora.json"
    );
  }
  public async closeBanner(banner: number) {
    return await this.delete<void>(`/users/app-banners/${banner}.json`);
  }

  public async updatePassword(id: number, passwords: UserPasswords) {
    return await this.put<{ incorrectPassword: boolean }>(
      `/users/password/${id}.json`,
      passwords
    );
  }

  public async resetPassword(email: string) {
    return await this.delete<{ ok: boolean }>(
      `/users/password.json?email=${email}`
    );
  }

  public async searchUsers(pid: number, searchUserRequest: SearchUserRequest) {
    return await this.post<SearchUserResponse>(
      `/projects/${pid}/users/search.json`,
      searchUserRequest
    );
  }
  public async getUser(pid: number, uid: number) {
    return await this.get<SearchUser>(`/projects/${pid}/users/${uid}.json`);
  }
  public async createUser(user: CreateUser) {
    const res = await this.post<{ user: Omit<SearchUser, "workspaces"> }>(
      "/users.json",
      user,
      true
    );
    return res.user;
  }

  // AVATAR
  public async deleteUserAvatar(user: AbstractUser, picture: Picture) {
    const userId = getId(user);
    const pictureId = getId(picture);
    return await this.delete<void>(
      `/users/${userId}/pictures/${pictureId}.json`
    );
  }
  public async uploadUserAvatar(user: AbstractUser, picture: File) {
    const userId = getId(user);
    const formData = new FormData();
    formData.append("image", picture);

    const { id, url } = await this.upload<{ id: number; url: string }>(
      `/users/${userId}/pictures.json`,
      formData
    );
    return { id, url };
  }
  public async uploadUserAvatars(user: AbstractUser, pictures: File[]) {
    return await Promise.all(
      pictures.map((picture) => this.uploadUserAvatar(user, picture))
    );
  }
  public async getUserAvatars(user: number) {
    const { pictures = [] } = await this.get<{
      pictures: { id: number; url: string }[];
    }>(`/users/${user}/pictures.json`);
    return pictures;
  }

  public async getDiscoveryPreferences(pid: number, workspaceId: number) {
    return await this.get<DiscoveryPreferenceResponse>(
      `/projects/${pid}/dashboards/${workspaceId}/discovery-preferences.json`
    );
  }

  public async updateDiscoveryPreferences(
    pid: number,
    workspaceId: number,
    params: DiscoveryPreferencePayload
  ) {
    return await this.post<DiscoveryPreferenceResponse>(
      `/projects/${pid}/dashboards/${workspaceId}/discovery-preferences.json`,
      params
    );
  }
}
